/**
 * The overview.
 *
 * Deliberately built from what the installation actually has. A dashboard that
 * shows a Finance tile on an installation without Finance is a dashboard that
 * lies — so every tile is behind the same `Can` check its screen is, and the
 * layout is whatever survives.
 *
 * The empty case is handled rather than left blank: a brand-new installation
 * with only `core` enabled gets a short explanation of what to do next, which
 * is the moment a self-hosted product is most likely to be abandoned.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, LifeBuoy, Settings, ShieldCheck, Users } from 'lucide-react';
import {
  Alert,
  Badge,
  Can,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  StatCard,
  StatRow,
  api,
  buttonClass,
  formatNumber,
  useCurrentUser,
  useHasService,
  useSession,
} from '@connect/web-shared';

export function DashboardPage() {
  const user = useCurrentUser();
  const services = useSession((state) => state.services);
  const hasCrm = useHasService('crm');

  // Counts come from the register endpoints with `page_size=1` — the total is
  // in the pagination meta, so a count costs one row rather than a dedicated
  // endpoint per tile.
  const clientCount = useQuery({
    queryKey: ['dashboard', 'clients', 'count'],
    queryFn: async () => {
      const page = await api.page<unknown>('/crm/clients', { params: { page_size: 1 } });
      return page.total;
    },
    enabled: hasCrm,
  });

  const activeClients = useQuery({
    queryKey: ['dashboard', 'clients', 'active'],
    queryFn: async () => {
      const page = await api.page<unknown>('/crm/clients', {
        params: {
          page_size: 1,
          filters: JSON.stringify({ status: { type: 'select', values: ['active'] } }),
        },
      });
      return page.total;
    },
    enabled: hasCrm,
  });

  // Only `core` means a freshly installed system that nobody has configured.
  const onlyCore = services.size <= 1;
  const firstName = user?.full_name.split(' ')[0] ?? 'there';

  return (
    <div className="space-y-5">
      <PageHeader
        title={`Good to see you, ${firstName}`}
        description="What is happening across this installation."
      />

      {onlyCore ? (
        <Alert tone="info" title="This installation has nothing switched on yet">
          <div className="space-y-2">
            <p>
              Only the core service is enabled, which covers sign-in, people and access, and the
              audit trail. Everything else — clients, projects, support, time tracking,
              invoicing — is switched off, so its screens are hidden and its API refuses calls.
            </p>
            <Can permission="core.service.manage">
              <Link to="/settings/services" className={buttonClass({ size: 'sm' })}>
                Choose services
                <ArrowRight className="size-3.5" />
              </Link>
            </Can>
          </div>
        </Alert>
      ) : null}

      <StatRow>
        <Can permission="crm.client.view">
          <StatCard
            label="Clients"
            // A dash, never a 0, until the count is known: a failed request
            // must not read as "no clients".
            value={clientCount.data === undefined ? '—' : formatNumber(clientCount.data)}
            hint={
              clientCount.isError
                ? 'Could not be read just now'
                : activeClients.data !== undefined
                  ? `${formatNumber(activeClients.data)} active`
                  : undefined
            }
            icon={<Building2 className="size-4" />}
            tone="accent"
            to="/clients"
          />
        </Can>
        <Can permission="iam.user.view">
          <StatCard
            label="Users"
            value="—"
            hint="People who can sign in"
            icon={<Users className="size-4" />}
            to="/access/users"
          />
        </Can>
        <StatCard
          label="Services enabled"
          value={formatNumber(services.size)}
          hint="Of what Connect offers"
          icon={<LifeBuoy className="size-4" />}
          tone="success"
          to="/settings/services"
        />
        <StatCard
          label="Your scope"
          value={user?.scope.kind === 'organization' ? 'Organization' : 'Scoped'}
          hint={
            user?.scope.kind === 'organization'
              ? 'You see everything'
              : 'You see your own part of the organization'
          }
          icon={<ShieldCheck className="size-4" />}
        />
      </StatRow>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Your access"
            description="What your roles resolve to in this scope."
          />
          <CardBody className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {[...services].sort().map((service) => (
                <Badge key={service} tone="accent">
                  {service}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted">
              You hold {formatNumber(user?.permissions.length ?? 0)} permissions across{' '}
              {formatNumber(services.size)} enabled service{services.size === 1 ? '' : 's'}.
              {user?.is_superuser ? ' You are a superuser, so permission checks are bypassed — but the Service Switch is not.' : ''}
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Getting set up" description="The usual order." />
          <CardBody>
            <ol className="space-y-2.5 text-sm">
              {[
                {
                  label: 'Choose your services',
                  to: '/settings/services',
                  done: !onlyCore,
                  hint: 'Decide what this installation is for.',
                },
                {
                  label: 'Add your people',
                  to: '/access/users',
                  done: false,
                  hint: 'Invite the employees who will use it.',
                },
                {
                  label: 'Set up roles',
                  to: '/access/roles',
                  done: false,
                  hint: 'Connect ships six; adjust them or add your own.',
                },
                {
                  label: 'Add your first client',
                  to: '/clients',
                  done: (clientCount.data ?? 0) > 0,
                  hint: 'Everything else hangs off a client.',
                },
              ].map((step, index) => (
                <li key={step.label} className="flex gap-2.5">
                  <span
                    className={
                      step.done
                        ? 'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-success text-2xs font-bold text-success-fg'
                        : 'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-surface-2 text-2xs font-bold text-faint'
                    }
                    aria-hidden
                  >
                    {step.done ? '✓' : index + 1}
                  </span>
                  <span className="min-w-0">
                    <Link to={step.to} className="font-medium text-accent hover:underline">
                      {step.label}
                    </Link>
                    <span className="block text-xs text-muted">{step.hint}</span>
                  </span>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>
      </div>

      <Can permission="core.setting.view">
        <Card>
          <CardHeader
            title="Installation"
            description="This is a self-hosted installation. Nothing here reaches anybody else's data."
            actions={
              <Link
                to="/settings"
                className={buttonClass({ variant: 'secondary', size: 'sm' })}
              >
                <Settings className="size-3.5" />
                Settings
              </Link>
            }
          />
        </Card>
      </Can>
    </div>
  );
}
